import dayjs from "dayjs";
import { CustomerGroups } from "@/types/general";
import { CustomerGroupsFormTyping, DiscountItem } from "./types";

/**
 * Joins the API's separate product and category collections into the discriminated item list used
 * by the mutation form and read-only table. Keeping this conversion at the module boundary prevents
 * the UI from carrying two synchronized field arrays.
 * Below there is an opposite function for splitting.
 */
export function getCustomerGroupDiscountItems(
    customerGroup: CustomerGroups.Extended
): DiscountItem[] {
    return [
        ...(customerGroup.products ?? []).map((entry) => ({
            type: "product" as const,
            product: entry.product,
            sale_price: entry.sale_price,
            valid_from: entry.valid_from,
            valid_to: entry.valid_to,
        })),
        ...(customerGroup.categories ?? []).map((entry) => ({
            type: "category" as const,
            category: entry.category,
            valid_from: entry.valid_from,
            valid_to: entry.valid_to,
        })),
    ];
}

/**
 * Produces a complete React Hook Form value object for both creation and edit hydration. Defaults
 * are defined here so every customer-group mutation starts from the same stable form shape.
 */
export function getCustomerGroupFormValues(
    customerGroup?: CustomerGroups.Extended
): CustomerGroupsFormTyping {
    return {
        code: customerGroup?.code ?? "",
        name: customerGroup?.name ?? "",
        discount: customerGroup?.discount ?? 0,
        customers: customerGroup?.customers.map((customer) => ({ value: customer })) ?? [],
        items: customerGroup ? getCustomerGroupDiscountItems(customerGroup) : [],
    };
}

/**
 * Splits the UI's discriminated item list back into the backend's products and categories arrays.
 * An omitted valid-from date keeps the existing customer-group behavior of becoming valid today.
 * Above there is an opposite function for joining.
 */
export function getCustomerGroupMutationPayload(
    formValues: CustomerGroupsFormTyping
): CustomerGroups.DTO.Create {
    const products: NonNullable<CustomerGroups.DTO.Create["products"]> = [];
    const categories: NonNullable<CustomerGroups.DTO.Create["categories"]> = [];

    formValues.items.forEach((item) => {
        const validity = {
            valid_from: item.valid_from?.format("YYYY-MM-DD") ?? dayjs().format("YYYY-MM-DD"),
            valid_to: item.valid_to?.format("YYYY-MM-DD"),
        };

        if (item.type === "product") {
            products.push({
                id: item.product.id,
                sale_price: item.sale_price,
                ...validity,
            });
            return;
        }

        categories.push({
            id: item.category.id,
            ...validity,
        });
    });

    return {
        code: formValues.code,
        name: formValues.name,
        discount: formValues.discount,
        customers: formValues.customers.map((customer) => customer.value.id),
        products,
        categories,
    };
}
